/**
 * bomLibraryApi.js — thin wrappers over Api_Functions for the BOM library and the workflow ↔ BOM
 * link (story 117). One function per Sun endpoint, named after the contract row (N1-N22) it maps to;
 * see mic-user-stories/stories/117-.../contrato-sun.md §1. No business logic lives here: this is the
 * only file that spells the `bomLibrary/*` and `workflow/{id}/bomLink*` routes.
 */
import { apiDelete, apiGet, apiPost, apiPut } from '../../generic/Api_Functions'

const withQuery = (path, params) => {
  const entries = Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== '')
  if (entries.length === 0) return path
  return path + '?' + new URLSearchParams(Object.fromEntries(entries)).toString()
}

// ---- Library (N1-N14) --------------------------------------------------------------------------

/** N1 — `POST bomLibrary/search`: RequestPagination + indexValues?, createdFrom?, createdTo?, componentCode? → Page<BomLibraryListItem> */
export const searchBomLibrary = (body) => apiPost('bomLibrary/search', body)

/** N2 — `GET bomLibrary/{code}` → BomLibraryDetail (versions DESC) */
export const getBomLibraryDetail = (code) => apiGet('bomLibrary/' + encodeURIComponent(code))

/** N3 — `GET bomLibrary/version/{bomId}/structure` → WorkflowResourceDto[] */
export const getBomVersionStructure = (bomId) => apiGet('bomLibrary/version/' + bomId + '/structure')

/** N4 — `POST bomLibrary/version/{bomId}/structure` RequestPagination → Page<WorkflowResourceDto> (sort/column filters ignored by Sun) */
export const searchBomVersionStructure = (bomId, pagination) => apiPost('bomLibrary/version/' + bomId + '/structure', pagination)

/** N5 — `GET bomLibrary/version/{bomId}/saveMode` → BomSaveModeOptions */
export const getBomVersionSaveMode = (bomId) => apiGet('bomLibrary/version/' + bomId + '/saveMode')

/** N6 — `POST bomLibrary/compare` {fromBomId?, toBomId} → BomComparison. Omitting fromBomId compares against n-1. */
export const compareBomVersions = ({ fromBomId, toBomId }) => {
  const body = { toBomId }
  if (fromBomId !== undefined && fromBomId !== null) body.fromBomId = fromBomId
  return apiPost('bomLibrary/compare', body)
}

/** N7 — `GET bomLibrary/version/{bomId}/usages` → BomUsage[] */
export const getBomVersionUsages = (bomId) => apiGet('bomLibrary/version/' + bomId + '/usages')

/**
 * N23 — `GET bomLibrary/version/{bomId}/lineUsage` → BomLineUsage[] (story 134).
 *
 * ONE entry per line of the version, including the ones nobody consumes (`operationCount: 0`), so
 * there is never a need to tell "absent" from "zero". It is a route of its own and not a field on
 * N3 because `WorkflowResourceDto` is frozen and shared with the workflow editor, the MOS, the
 * fabrication order and Compass: the two lists are matched by `componentUuid`, and the ORDER of this
 * one is not contract.
 */
export const getBomVersionLineUsage = (bomId) =>
  apiGet('bomLibrary/version/' + bomId + '/lineUsage')

/** N8 — `POST bomLibrary` {code, name, description?, indexValue} → 201 BomVersion */
export const createBom = (body) => apiPost('bomLibrary', body)

/** N9 — `PUT bomLibrary/version/{bomId}` BomVersionSaveRequest → BomVersionSaveResult */
export const saveBomVersion = (bomId, body) => apiPut('bomLibrary/version/' + bomId, body)

/** N10 — `PUT bomLibrary/version/{bomId}/indexValue` {indexValue} → BomVersion */
export const changeBomVersionIndex = (bomId, indexValue) => apiPut('bomLibrary/version/' + bomId + '/indexValue', { indexValue })

/** N11 — `DELETE bomLibrary/version/{bomId}` → 204 · 409 {error, usages} */
export const deleteBomVersion = (bomId) => apiDelete('bomLibrary/version/' + bomId)

/** N12 — `DELETE bomLibrary/{code}` → 204 · 409 {error, usagesByVersion} */
export const deleteBom = (code) => apiDelete('bomLibrary/' + encodeURIComponent(code))

/** N13 — `POST bomLibrary/import/{importScopeUUID}/commit` BomImportCommitRequest → BomVersionSaveResult */
export const commitBomImport = (importScopeUUID, body) => apiPost('bomLibrary/import/' + importScopeUUID + '/commit', body)

/** N14 — `DELETE bomLibrary/import/{importScopeUUID}` → 204 (also when the scope does not exist) */
export const discardBomImport = (importScopeUUID) => apiDelete('bomLibrary/import/' + importScopeUUID)

// ---- Workflow ↔ BOM link (N15-N22) --------------------------------------------------------------

/** N15 — `GET workflow/{workflowId}/bomLink` → WorkflowBomLink */
export const getWorkflowBomLink = (workflowId) => apiGet('workflow/' + workflowId + '/bomLink')

/** N16 — `POST workflow/{workflowId}/bomLink` {bomId, confirmDiscardEmbedded} → WorkflowBomLink · 409 E-LNK-01/02 */
export const linkWorkflowBom = (workflowId, body) => apiPost('workflow/' + workflowId + '/bomLink', body)

/** N17 — `DELETE workflow/{workflowId}/bomLink[?tempWorkflowUUID=]` → 204 · 409 E-LNK-03 */
export const unlinkWorkflowBom = (workflowId, tempWorkflowUUID) =>
  apiDelete(withQuery('workflow/' + workflowId + '/bomLink', { tempWorkflowUUID }))

/** N18 — `POST workflow/{workflowId}/bomLink/dismiss` {versionNumber} → WorkflowBomLink */
export const dismissWorkflowBomUpdate = (workflowId, versionNumber) =>
  apiPost('workflow/' + workflowId + '/bomLink/dismiss', { versionNumber })

/** N19 — `GET workflow/{workflowId}/bomLink/updateImpact/{targetBomId}` → BomUpdateImpact */
export const getWorkflowBomUpdateImpact = (workflowId, targetBomId) =>
  apiGet('workflow/' + workflowId + '/bomLink/updateImpact/' + targetBomId)

/** N20 — `POST workflow/{workflowId}/bomLink/update` {targetBomId} → BomUpdateResult */
export const updateWorkflowBom = (workflowId, targetBomId) =>
  apiPost('workflow/' + workflowId + '/bomLink/update', { targetBomId })

/** N21 — `POST workflow/{workflowId}/bomLibrary/publish[?tempWorkflowUUID=]` {code, name, description?, asNewVersionOf?} → BomPublishResult */
export const publishWorkflowBom = (workflowId, body, tempWorkflowUUID) =>
  apiPost(withQuery('workflow/' + workflowId + '/bomLibrary/publish', { tempWorkflowUUID }), body)

/** N22 — `GET workflow/{workflowId}/bomLibrary/publishPreview?code=[&tempWorkflowUUID=]` → BomPublishPreview */
export const previewWorkflowBomPublish = (workflowId, code, tempWorkflowUUID) =>
  apiGet(withQuery('workflow/' + workflowId + '/bomLibrary/publishPreview', { code, tempWorkflowUUID }))

// ---- Errors -------------------------------------------------------------------------------------

/**
 * Normalises an error thrown by Api_Functions into `{ status, error, context }`, where `error` is the
 * verbatim business literal of contrato-sun.md §4 (empty for the bodiless 403s) and `context` holds the
 * extra fields of that case (`existingBomId`, `allowedSaveModes`, `usages`, `invalidLines`, ...).
 */
export const readBomLibraryError = (thrown) => {
  const data = thrown && typeof thrown.data === 'object' && thrown.data !== null ? thrown.data : {}
  const { error, ...context } = data
  return {
    status: thrown && thrown.status ? thrown.status : 0,
    error: typeof error === 'string' ? error : '',
    context
  }
}
